/**
 * Alert Context module for managing alert state and notifications.
 *
 * This module provides a React Context and Provider for centralized alert management
 * across the application. It allows components to display temporary alert messages
 * with different types (info, error, success, warning) that automatically disappear
 * after a configurable timeout period.
 *
 * Key exports:
 * - AlertContext: The context object for alert state and setAlert function
 * - AlertProvider: The provider component that wraps the application to enable alert functionality
 *
 * @module components/common/AlertContext
 * @author Snowfly
 * @version 1.0.0
 */
import React, { createContext, useState } from 'react';
import PropTypes from "prop-types";

const ALERT_TIME = 5000;
const initialState = {
    text: '',
    type: '',
    time: ALERT_TIME
};

/**
 * Context for managing alert messages within the application.
 *
 * AlertContext provides a centralized mechanism to handle alert-related state
 *  and operations, such as displaying messages of various types (e.g., info,
 *  error, success, warning).
 *
 * The context includes a state object and a `setAlert` function to trigger alerts.
 */
const AlertContext = createContext({
    ...initialState,
    /**
     *
     * @param text String
     * @param type String <'info'|'error'|'success'|'warning'>
     * @param time Number
     */
    setAlert: (text, type, time) => {}, // eslint-disable-line no-unused-vars
});

/**
 * A context provider that allows children components to access and manage alert
 *  notifications.
 * The provider manages alert text and type, and provides a method for setting alerts.
 * Alerts automatically reset after a predefined timeout.
 *
 * @component
 * @param {Object} props - Props passed to the component.
 * @param {React.ReactNode} props.children - The child components that will have
 *      access to the alert context.
 * @returns {JSX.Element} The AlertContext.Provider wrapping the child components.
 */
export const AlertProvider = ({ children }) => {
    const [text, setText] = useState('');
    const [type, setType] = useState('');
    const [duration, setDuration] = useState(ALERT_TIME);

    /**
     * Updates the alert message and type, then clears them after a predefined
     *  duration.
     *
     * @param {string} text - The text message to display in the alert.
     * @param {string} type - The type/category of the alert (e.g., "success",
     *      "error", "info", "warning").
     * @param {number} time - The duration in milliseconds before the alert goes away.
     */
    const setAlert = (text, type, time= 5000) => {
        setText(text);
        setType(type);
        setDuration(time);

        setTimeout(() => {
            setText('');
            setType('');
        }, duration);
    };

    return (
        <AlertContext.Provider
            value={{
                text,
                type,
                duration,
                setAlert,
            }}
        >
            {children}
        </AlertContext.Provider>
    );
};

AlertProvider.propTypes = {
    children: PropTypes.node.isRequired,
}

export default AlertContext;
